import type { MetricCanonicalName, MetricInfo, StepPlanInfo } from '@az3fr/core';
import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from '@/lib/store';
import { exerciseSliceSelectors } from '@/entities/exercise/model/exercise.selector';
import { metricSliceSelectors } from '@/entities/metrics/model/metric.selector';
import { stepPlanAdapter } from '@/entities/step-plan/model/step-plan.slice';

const baseSelectors = stepPlanAdapter.getSelectors((state: RootState) => state.stepPlans);

const selectAllBySectionPlanId = createSelector(
	[
		baseSelectors.selectAll,
		exerciseSliceSelectors.selectAllPopulated,
		(_, sectionPlanId) => sectionPlanId,
	],
	(allStepPlans, allPopulatedExercises, sectionPlanId) => {
		if (sectionPlanId === null || sectionPlanId === undefined) {
			return [];
		}

		return (
			allStepPlans
				.filter((step) => step.sectionPlanId === sectionPlanId)
				// For each step, find the exercise
				.map((step) => {
					const exercise = allPopulatedExercises.find(
						(exercise) => exercise.id === step.exerciseId,
					);
					if (!exercise) {
						console.error('Exercise not found', step.exerciseId);
						return {
							...step,
							exercise: null,
						};
					}
					// Return the step plan with the exercise
					return {
						...step,
						exercise,
					};
				})
		);
	},
);

const selectPopulatedTrackedMetricsByStepPlanId = createSelector(
	[baseSelectors.selectAll, metricSliceSelectors.selectAll, (_, stepPlanId) => stepPlanId],
	(allStepPlans, allMetrics, stepPlanId): MetricInfo[] => {
		const step = allStepPlans.find((step: StepPlanInfo) => step.id === stepPlanId);
		if (!step) {
			// Don't log error during deletion - this is expected behavior
			return [];
		}

		return step.trackedMetrics
			.map((metricCanonicalName: MetricCanonicalName): MetricInfo | null => {
				const metric = allMetrics.find(
					(metric: MetricInfo) => metric.canonicalName === metricCanonicalName,
				);
				if (!metric) {
					console.error('Metric not found', step, metricCanonicalName);
					return null;
				}
				return metric;
			})
			.filter((metric: MetricInfo | null) => metric !== null)
			.sort((a: MetricInfo, b: MetricInfo) => a.displayOrder - b.displayOrder);
	},
);

const stepPlanSliceSelectors = {
	...baseSelectors,
	selectAllBySectionPlanId,
	selectPopulatedTrackedMetricsByStepPlanId,
};

export { stepPlanSliceSelectors };
